import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Description,
  Drawer,
  FieldError,
  ListBox,
  useOverlayState,
  type OverlayTriggerState,
} from '@heroui/react';
import { MapPin } from 'lucide-react';
import { AsyncButton } from '../../../../components/ui/async-button';
import { FloorPlanDrawer } from '../../../../components/floor-plan/floor-plan-drawer';
import {
  floorPlanRectFromLocation,
  formatFloorPlanRect,
  hasFloorPlanLocation,
  type FloorPlanRect,
} from '../../../../components/floor-plan/floor-plan-utils';
import { StatusCallout } from '../../../../components/ui/status-callout';
import type {
  Apartment,
  ParkingSpot,
  ParkingSpotFormPayload,
  RentStatusMode,
} from '../../../../api/condominiums';
import {
  DrawerSelectField,
  DrawerTextAreaField,
  DrawerTextField,
} from '../../../../components/ui/drawer-form-field';
import { FormSwitch } from '../../../../components/ui/form-switch';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../../components/ui/side-drawer-classes';
import { sortApartments } from '../../utils/sort-apartments';

interface SpotFormDrawerProps {
  state: OverlayTriggerState;
  editing: { spot: ParkingSpot; blockId: string } | null;
  blockId: string | null;
  apartments: Apartment[];
  onSubmit: (blockId: string, payload: ParkingSpotFormPayload) => Promise<void>;
}

const defaultForm: ParkingSpotFormPayload = {
  code: '',
  location: '',
  notes: '',
  level: '',
  status: 'OCCUPIED',
  rentStatusMode: 'MARK_AS_RENTED',
  isActive: true,
  apartmentId: '',
};

function resolveApartmentId(
  preferredId: string | undefined,
  apartments: Apartment[],
): string {
  if (preferredId && apartments.some((apt) => apt.id === preferredId)) {
    return preferredId;
  }
  const active = apartments.find((apt) => apt.isActive);
  if (active) return active.id;
  return apartments[0]?.id ?? '';
}

export function SpotFormDrawer({
  state,
  editing,
  blockId,
  apartments,
  onSubmit,
}: SpotFormDrawerProps) {
  const [form, setForm] = useState<ParkingSpotFormPayload>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const floorPlanDrawer = useOverlayState();

  const apartmentsForSelect = useMemo(() => sortApartments(apartments), [apartments]);

  useEffect(() => {
    if (!state.isOpen) return;

    const preferredApartmentId = editing?.spot.apartmentId;
    setForm({
      code: editing?.spot.code ?? '',
      location: editing?.spot.location ?? '',
      notes: editing?.spot.notes ?? '',
      level: editing?.spot.level ?? '',
      status: editing?.spot.status ?? 'OCCUPIED',
      rentStatusMode: editing?.spot.rentStatusMode ?? 'MARK_AS_RENTED',
      isActive: editing?.spot.isActive ?? true,
      apartmentId: preferredApartmentId ?? '',
      locationX:
        editing?.spot.locationX != null ? Number(editing.spot.locationX) : undefined,
      locationY:
        editing?.spot.locationY != null ? Number(editing.spot.locationY) : undefined,
      locationWidth:
        editing?.spot.locationWidth != null ? Number(editing.spot.locationWidth) : undefined,
      locationHeight:
        editing?.spot.locationHeight != null ? Number(editing.spot.locationHeight) : undefined,
    });
    setError(null);
    setSubmitting(false);
  }, [state.isOpen, editing?.spot.id]);

  useEffect(() => {
    if (!state.isOpen || apartments.length === 0) return;

    setForm((prev) => {
      if (prev.apartmentId && apartments.some((apt) => apt.id === prev.apartmentId)) {
        return prev;
      }
      return {
        ...prev,
        apartmentId: resolveApartmentId(editing?.spot.apartmentId, apartments),
      };
    });
  }, [state.isOpen, apartments, editing?.spot.apartmentId]);

  useEffect(() => {
    if (!state.isOpen) {
      floorPlanDrawer.close();
    }
  }, [state.isOpen]);

  const isValid = form.code.trim().length > 0 && !!form.apartmentId;
  const canSubmit =
    isValid && (editing !== null || apartmentsForSelect.some((a) => a.isActive));

  const handleSubmit = async () => {
    if (!blockId || !canSubmit || submitting) return;

    if (!form.apartmentId) {
      setError('Selecione um apartamento para vincular a vaga.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(blockId, {
        ...form,
        code: form.code.trim(),
        apartmentId: form.apartmentId,
        location: form.location?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
        level: form.level?.trim() || undefined,
        locationX: form.locationX ?? null,
        locationY: form.locationY ?? null,
        locationWidth: form.locationWidth ?? null,
        locationHeight: form.locationHeight ?? null,
      });
      state.close();
    } catch (err) {
      let message = 'Não foi possível salvar a vaga.';
      if (typeof err === 'object' && err !== null && 'message' in err) {
        message = String((err as { message: unknown }).message);
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const applyFloorPlanRect = (rect: FloorPlanRect | null) => {
    setForm((prev) => ({
      ...prev,
      locationX: rect?.x ?? null,
      locationY: rect?.y ?? null,
      locationWidth: rect?.width ?? null,
      locationHeight: rect?.height ?? null,
    }));
  };

  const floorPlanRect = floorPlanRectFromLocation(form);
  const hasMark = hasFloorPlanLocation(form);

  return (
    <>
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>{editing ? 'Editar vaga' : 'Nova vaga'}</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={`${drawerBodyClassName} gap-5`}>
              {error ? <FieldError>{error}</FieldError> : null}

              <div className="flex flex-col gap-4">
                <Description className="font-medium text-foreground">Informações básicas</Description>

                {apartmentsForSelect.length === 0 ? (
                  <FieldError>
                    Cadastre pelo menos um apartamento neste bloco antes de criar a vaga.
                  </FieldError>
                ) : (
                  <DrawerSelectField
                    label="Apartamento"
                    placeholder="Selecione um apartamento"
                    value={form.apartmentId || null}
                    onChange={(apartmentId) =>
                      setForm((prev) => ({
                        ...prev,
                        apartmentId,
                      }))
                    }
                    isDisabled={submitting}
                    isRequired
                    description="Vaga vinculada a este apartamento."
                  >
                    <ListBox>
                      {apartmentsForSelect.map((apt) => {
                        const label = `${apt.number}${apt.floor ? ` — Andar ${apt.floor}` : ''}${!apt.isActive ? ' (inativo)' : ''}`;
                        const isItemDisabled = !apt.isActive && apt.id !== form.apartmentId;
                        return (
                          <ListBox.Item
                            key={apt.id}
                            id={apt.id}
                            textValue={label}
                            isDisabled={isItemDisabled}
                          >
                            {label}
                            <ListBox.ItemIndicator />
                          </ListBox.Item>
                        );
                      })}
                    </ListBox>
                  </DrawerSelectField>
                )}

                <DrawerTextField
                  label="Código da vaga"
                  value={form.code}
                  placeholder="Ex: G12, V-15"
                  onChange={(code) => setForm((prev) => ({ ...prev, code }))}
                  isDisabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-4 border-t border-separator pt-4">
                <Description className="font-medium text-foreground">Localização</Description>
                <DrawerTextField
                  label="Localização (opcional)"
                  value={form.location ?? ''}
                  placeholder="Ex: Perto da entrada"
                  onChange={(location) => setForm((prev) => ({ ...prev, location }))}
                  isDisabled={submitting}
                />
                <DrawerTextField
                  label="Nível (opcional)"
                  value={form.level ?? ''}
                  placeholder="Ex: Térreo, Sub-solo"
                  onChange={(level) => setForm((prev) => ({ ...prev, level }))}
                  isDisabled={submitting}
                />
                <Button
                  variant="secondary"
                  isDisabled={submitting}
                  onPress={floorPlanDrawer.open}
                >
                  <MapPin size={16} />
                  {hasMark ? 'Editar marcação na planta' : 'Marcar localização na planta'}
                </Button>
                {hasMark && floorPlanRect ? (
                  <StatusCallout status="success">
                    Área marcada na planta ({formatFloorPlanRect(floorPlanRect)})
                  </StatusCallout>
                ) : null}
              </div>

              <div className="flex flex-col gap-4 border-t border-separator pt-4">
                <Description className="font-medium text-foreground">Observações</Description>
                <DrawerTextAreaField
                  label="Notas"
                  value={form.notes ?? ''}
                  placeholder="Informações adicionais"
                  onChange={(notes) => setForm((prev) => ({ ...prev, notes }))}
                  isDisabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-4 border-t border-separator pt-4">
                <Description className="font-medium text-foreground">Configurações</Description>
                <DrawerSelectField
                  label="Modo de status de aluguel"
                  value={form.rentStatusMode ?? 'MARK_AS_RENTED'}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      rentStatusMode: value as RentStatusMode,
                    }))
                  }
                  isDisabled={submitting}
                >
                  <ListBox>
                    <ListBox.Item id="MARK_AS_RENTED" textValue="Marcar como alugada">
                      Marcar como alugada
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                    <ListBox.Item id="KEEP_OCCUPIED" textValue="Manter como ocupada">
                      Manter como ocupada
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  </ListBox>
                </DrawerSelectField>

                <FormSwitch
                  label="Vaga ativa"
                  isSelected={form.isActive ?? true}
                  onChange={(checked) => setForm((prev) => ({ ...prev, isActive: checked }))}
                  isDisabled={submitting}
                />
              </div>
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" className="flex-1" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                className="flex-1"
                isPending={submitting}
                isDisabled={!canSubmit}
                pendingLabel={editing ? 'Salvar' : 'Criar vaga'}
                onPress={handleSubmit}
              >
                {editing ? 'Salvar' : 'Criar vaga'}
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>

    <FloorPlanDrawer
      state={floorPlanDrawer}
      title={
        form.code.trim()
          ? `Marcar localização na planta · ${form.code.trim()}`
          : 'Marcar localização na planta'
      }
      subtitle="Toque ou arraste na planta para marcar a área da vaga"
      rect={floorPlanRect}
      editable
      onConfirm={applyFloorPlanRect}
    />
    </>
  );
}
